import { useState, useEffect } from 'react';
import { Cookie, X, Settings, Check } from 'lucide-react';
import { Button } from './ui/button';
import { Link } from 'react-router-dom';
import { useSiteContent } from '../context/SiteContentContext';

export const CookieConsent = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [showCustomize, setShowCustomize] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true,
    analytics: false,
    marketing: false,
    functional: false
  });
  const { language, localizedPath } = useSiteContent();

  useEffect(() => {
    const consent = localStorage.getItem('cookieConsent');
    if (!consent) {
      const timer = setTimeout(() => setIsVisible(true), 1000);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleAcceptAll = () => {
    const allAccepted = { necessary: true, analytics: true, marketing: true, functional: true };
    localStorage.setItem('cookieConsent', 'all');
    localStorage.setItem('cookiePreferences', JSON.stringify(allAccepted));
    setIsVisible(false);
  };

  const handleRejectNonEssential = () => {
    const essentialOnly = { necessary: true, analytics: false, marketing: false, functional: false };
    localStorage.setItem('cookieConsent', 'essential');
    localStorage.setItem('cookiePreferences', JSON.stringify(essentialOnly));
    setIsVisible(false);
  };

  const handleSavePreferences = () => {
    localStorage.setItem('cookieConsent', 'custom');
    localStorage.setItem('cookiePreferences', JSON.stringify(preferences));
    setIsVisible(false);
  };

  const handleDecline = () => {
    localStorage.setItem('cookieConsent', 'declined');
    localStorage.setItem('cookiePreferences', JSON.stringify({ necessary: true, analytics: false, marketing: false, functional: false }));
    setIsVisible(false);
  };

  const labels = language === 'bg' ? {
    title: 'Използваме бисквитки',
    description: 'Използваме бисквитки за подобряване на сърфирането.',
    rejectNonEssential: 'Откажи незадължителните',
    customize: 'Настройки',
    acceptAll: 'Приеми всички',
    decline: 'Откажи',
    savePreferences: 'Запази',
    cookiePreferences: 'Настройки на бисквитките',
    necessary: 'Необходими',
    necessaryDesc: 'Необходими за работата на сайта',
    alwaysActive: 'Винаги',
    analytics: 'Аналитични',
    analyticsDesc: 'Помагат ни да разберем как използвате сайта',
    marketing: 'Маркетингови',
    marketingDesc: 'За показване на релевантни реклами',
    functional: 'Функционални',
    functionalDesc: 'Подобряват функционалността на сайта',
    privacyPolicy: 'Политика за поверителност',
    cookiePolicy: 'Политика за бисквитки'
  } : {
    title: 'We use cookies',
    description: 'We use cookies to enhance your browsing experience.',
    rejectNonEssential: 'Reject non-essential',
    customize: 'Customize',
    acceptAll: 'Accept All',
    decline: 'Decline',
    savePreferences: 'Save',
    cookiePreferences: 'Cookie Preferences',
    necessary: 'Necessary',
    necessaryDesc: 'Essential for the website to function',
    alwaysActive: 'Always',
    analytics: 'Analytics',
    analyticsDesc: 'Help us understand how you use the site',
    marketing: 'Marketing',
    marketingDesc: 'Used to show relevant advertisements',
    functional: 'Functional',
    functionalDesc: 'Enhance the functionality of the site',
    privacyPolicy: 'Privacy Policy',
    cookiePolicy: 'Cookie Policy'
  };

  if (!isVisible) return null;

  return (
    <div 
      data-testid="cookie-consent-banner"
      className="fixed bottom-0 left-0 right-0 z-[100] p-4 bg-white border-t border-slate-200 shadow-2xl animate-slide-up"
    >
      <div className="max-w-4xl mx-auto relative">
        <button
          onClick={handleDecline}
          className="absolute -top-1 right-0 p-1 text-slate-400 hover:text-slate-600"
          aria-label="Close"
        >
          <X className="w-5 h-5" />
        </button>

        {!showCustomize ? (
          /* Main View */
          <div className="space-y-4">
            <div className="flex items-start gap-3 pr-8">
              <div className="w-10 h-10 bg-red-50 rounded-full flex items-center justify-center flex-shrink-0">
                <Cookie className="w-5 h-5 text-[#C41E3A]" />
              </div>
              <div>
                <h3 className="text-slate-900 font-bold text-sm md:text-base mb-1">{labels.title}</h3>
                <p className="text-slate-500 text-xs md:text-sm">
                  {labels.description}
                </p>
                <div className="flex flex-wrap gap-1 mt-1">
                  <Link to={localizedPath('/legal/cookie-policy')} className="text-[#C41E3A] hover:underline text-xs">{labels.cookiePolicy}</Link>
                  <span className="text-slate-300 text-xs">|</span>
                  <Link to={localizedPath('/legal/privacy-policy')} className="text-[#C41E3A] hover:underline text-xs">{labels.privacyPolicy}</Link>
                </div>
              </div>
            </div>
            
            <div className="flex flex-wrap gap-2">
              <Button
                onClick={handleRejectNonEssential}
                variant="outline"
                data-testid="cookie-reject-btn"
                className="flex-1 sm:flex-none rounded-full border-slate-300 text-slate-600 hover:bg-slate-100 text-xs sm:text-sm h-9 px-3"
              >
                {labels.rejectNonEssential}
              </Button>
              <Button
                onClick={() => setShowCustomize(true)}
                variant="outline"
                data-testid="cookie-customize-btn"
                className="flex-1 sm:flex-none rounded-full border-slate-300 text-slate-600 hover:bg-slate-100 text-xs sm:text-sm h-9 px-3"
              >
                <Settings className="w-3 h-3 mr-1" />
                {labels.customize}
              </Button>
              <Button
                onClick={handleAcceptAll}
                data-testid="cookie-accept-btn"
                className="flex-1 sm:flex-none bg-[#C41E3A] text-white hover:bg-[#A01830] rounded-full text-xs sm:text-sm h-9 px-3"
              >
                {labels.acceptAll}
              </Button>
            </div>
          </div>
        ) : (
          /* Customize View */
          <div className="space-y-3">
            <div className="flex items-center justify-between pr-6">
              <div className="flex items-center gap-2">
                <Cookie className="w-5 h-5 text-[#C41E3A]" />
                <h3 className="text-slate-900 font-bold text-sm">{labels.cookiePreferences}</h3>
              </div>
              <button onClick={() => setShowCustomize(false)} className="text-slate-400 hover:text-slate-600">
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {/* Necessary - Always Active */}
              <div className="flex items-center gap-3 p-2 sm:p-3 bg-slate-100 rounded-xl">
                <div className="w-5 h-5 rounded bg-[#C41E3A] flex items-center justify-center flex-shrink-0">
                  <Check className="w-3 h-3 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-1">
                    <span className="text-xs sm:text-sm font-medium text-slate-900">{labels.necessary}</span>
                    <span className="text-[9px] sm:text-[10px] text-green-600 bg-green-50 px-1.5 py-0.5 rounded-full whitespace-nowrap">{labels.alwaysActive}</span>
                  </div>
                  <p className="text-[10px] sm:text-xs text-slate-500 mt-0.5 line-clamp-1">{labels.necessaryDesc}</p>
                </div>
              </div>

              {/* Analytics */}
              <label className="flex items-center gap-3 p-2 sm:p-3 bg-slate-50 rounded-xl cursor-pointer hover:bg-slate-100 transition-colors">
                <input
                  type="checkbox"
                  checked={preferences.analytics}
                  onChange={(e) => setPreferences({...preferences, analytics: e.target.checked})}
                  className="w-4 h-4 sm:w-5 sm:h-5 rounded border-slate-300 text-[#C41E3A] focus:ring-[#C41E3A]"
                />
                <div className="flex-1 min-w-0">
                  <span className="text-xs sm:text-sm font-medium text-slate-900 block">{labels.analytics}</span>
                  <p className="text-[10px] sm:text-xs text-slate-500 mt-0.5 line-clamp-1">{labels.analyticsDesc}</p>
                </div>
              </label>

              {/* Marketing */}
              <label className="flex items-center gap-3 p-2 sm:p-3 bg-slate-50 rounded-xl cursor-pointer hover:bg-slate-100 transition-colors">
                <input
                  type="checkbox"
                  checked={preferences.marketing}
                  onChange={(e) => setPreferences({...preferences, marketing: e.target.checked})}
                  className="w-4 h-4 sm:w-5 sm:h-5 rounded border-slate-300 text-[#C41E3A] focus:ring-[#C41E3A]"
                />
                <div className="flex-1 min-w-0">
                  <span className="text-xs sm:text-sm font-medium text-slate-900 block">{labels.marketing}</span>
                  <p className="text-[10px] sm:text-xs text-slate-500 mt-0.5 line-clamp-1">{labels.marketingDesc}</p>
                </div>
              </label>

              {/* Functional */}
              <label className="flex items-center gap-3 p-2 sm:p-3 bg-slate-50 rounded-xl cursor-pointer hover:bg-slate-100 transition-colors">
                <input
                  type="checkbox"
                  checked={preferences.functional}
                  onChange={(e) => setPreferences({...preferences, functional: e.target.checked})}
                  className="w-4 h-4 sm:w-5 sm:h-5 rounded border-slate-300 text-[#C41E3A] focus:ring-[#C41E3A]"
                />
                <div className="flex-1 min-w-0">
                  <span className="text-xs sm:text-sm font-medium text-slate-900 block">{labels.functional}</span>
                  <p className="text-[10px] sm:text-xs text-slate-500 mt-0.5 line-clamp-1">{labels.functionalDesc}</p>
                </div>
              </label>
            </div>

            <div className="flex flex-wrap gap-2 pt-2 border-t border-slate-200">
              <Button
                onClick={handleDecline}
                variant="outline"
                className="flex-1 sm:flex-none rounded-full border-slate-300 text-slate-600 hover:bg-slate-100 text-xs h-8 px-3"
              >
                {labels.decline}
              </Button>
              <Button
                onClick={handleSavePreferences}
                variant="outline"
                className="flex-1 sm:flex-none rounded-full border-[#C41E3A] text-[#C41E3A] hover:bg-red-50 text-xs h-8 px-3"
              >
                {labels.savePreferences}
              </Button>
              <Button
                onClick={handleAcceptAll}
                className="flex-1 sm:flex-none bg-[#C41E3A] text-white hover:bg-[#A01830] rounded-full text-xs h-8 px-3"
              >
                {labels.acceptAll}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
